import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Container, FormGroup, Input } from "reactstrap";
import { Form, Field } from "react-final-form";
import axios from "axios";
import { connect } from "react-redux";
import { useDispatch, flashSuccess, flash422 } from "@/shared/components/flash";
import {
  isBadgeAward,
  isCustomAward,
  labelizeNamedData,
  makeLabelizedOptionsFromTree,
  toCurrency,
  unpatchSelect,
    getAwardMessage,
} from "@/shared/helpers";
import { getProgramTree } from "@/services/program/getProgramTree";
import { getProgram } from "@/services/program/getProgram";
import TemplateButton from "@/shared/components/TemplateButton";
import getUnitNumbers from "@/services/program/getUnitNumbers";
import { getEvent } from "@/services/program/getEvent";
import { getBalance } from "@/services/program/getBalance";
import getAwardLevels from "@/services/program/getAwardLevels";

const findOption = (options, value) =>
  options.find((option) => String(option.value) === String(value)) || null;

const stripProgramPrefix = (label) => label?.replace(/^[-\s]+/, "").trim() || "";

// The global axios response interceptor (src/containers/App/App.jsx) resolves
// 4xx/5xx responses instead of rejecting them and flashes the error itself,
// tagging them with `handled: true`. Callers must check for this explicitly —
// a rejected promise is not how API errors surface here.
const isHandledErrorResponse = (response) =>
  response?.handled === true ||
  (typeof response?.status === "number" && response.status >= 400);

const AwardNewParticipant = ({ organization, rootProgram, activeProgram }) => {
  const [programOptions, setProgramOptions] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedProgramData, setSelectedProgramData] = useState(activeProgram || null);
  const [baseValues, setBaseValues] = useState({});
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitPhase, setSubmitPhase] = useState("");
  const [submissionMessage, setSubmissionMessage] = useState("");
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [balance, setBalance] = useState(0);
  const [unitNumberOptions, setUnitNumberOptions] = useState([]);
  const [awardLevelOptions, setAwardLevelOptions] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    let isMounted = true;

    if (!rootProgram?.id) {
      setLoadingPrograms(false);
      return undefined;
    }

    setLoadingPrograms(true);

    getProgramTree(rootProgram.organization_id, rootProgram.id)
      .then((programTree) => {
        if (!isMounted) return;

        const nextOptions = makeLabelizedOptionsFromTree(programTree);
        const defaultProgram =
          findOption(nextOptions, activeProgram?.id) ||
          findOption(nextOptions, rootProgram.id) ||
          nextOptions[0] ||
          null;

        setProgramOptions(nextOptions);
        setSelectedProgram(defaultProgram);
        setBaseValues({
          program: defaultProgram,
          event_id: null,
          message: "",
          award_level: null,
          unit_number: null,
        });
        setLoadingPrograms(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setProgramOptions([]);
        setLoadingPrograms(false);
      });

    return () => {
      isMounted = false;
    };
  }, [activeProgram?.id, rootProgram]);

  const selectedProgramId = selectedProgram?.value ?? null;

  useEffect(() => {
    let isMounted = true;

    if (!organization?.id || !selectedProgramId) {
      setSelectedProgramData(null);
      return undefined;
    }

    if (String(activeProgram?.id) === String(selectedProgramId)) {
      setSelectedProgramData(activeProgram);
      return undefined;
    }

    setSelectedProgramData(null);

    getProgram(organization.id, selectedProgramId)
      .then((programData) => {
        if (!isMounted) return;
        setSelectedProgramData(programData);
      })
      .catch(() => {
        if (!isMounted) return;
        setSelectedProgramData(null);
      });

    return () => {
      isMounted = false;
    };
  }, [activeProgram, organization?.id, selectedProgramId]);

  const isPayInAdvance = useMemo(() => {
    if (selectedProgramData?.is_pay_in_advance === true) return true;
    return String(selectedProgramData?.is_pay_in_advance) === "1";
  }, [selectedProgramData]);

  useEffect(() => {
    if (!organization?.id || !selectedProgramId || !isPayInAdvance) {
      setBalance(0);
      return;
    }

    getBalance(organization.id, selectedProgramId)
      .then((data) => {
        setBalance(data);
      })
      .catch(() => {
        setBalance(0);
      });
  }, [isPayInAdvance, organization?.id, selectedProgramId]);

  const refreshBalance = async () => {
    if (!organization?.id || !selectedProgramId || !isPayInAdvance) {
      return 0;
    }

    try {
      const data = await getBalance(organization.id, selectedProgramId);
      // The global axios interceptor resolves error responses instead of
      // rejecting them, so a failed balance fetch can land here as a
      // non-numeric error payload rather than throwing — guard against that.
      const numericBalance = typeof data === "number" ? data : parseFloat(data);

      if (isNaN(numericBalance)) {
        setBalance(0);
        return 0;
      }

      setBalance(numericBalance);
      return numericBalance;
    } catch (error) {
      setBalance(0);
      return 0;
    }
  };

  useEffect(() => {
    if (!organization?.id || !selectedProgramId) {
      setAwardLevelOptions([]);
      return;
    }

    getAwardLevels(organization.id, selectedProgramId)
      .then((response) => {
        setAwardLevelOptions(labelizeNamedData(response) || []);
      })
      .catch(() => {
        setAwardLevelOptions([]);
      });
  }, [organization?.id, selectedProgramId]);

  useEffect(() => {
    if (!organization?.id || !selectedProgramId) {
      setUnitNumberOptions([]);
      return;
    }

    getUnitNumbers(organization.id, selectedProgramId, "assignable=1")
      .then((response) => {
        setUnitNumberOptions(labelizeNamedData(response) || []);
      })
      .catch(() => {
        setUnitNumberOptions([]);
      });
  }, [organization?.id, selectedProgramId]);

  // Invite and Award always awards against the program's configured Referral
  // Submission Award event — never a general event pick — so a manager using
  // this screen can't accidentally issue an unrelated award type here (ICOV3-1780).
  const referralEventId = selectedProgramData?.referral_submission_award_event_id || null;

  useEffect(() => {
    let isMounted = true;

    if (!organization?.id || !selectedProgramId || !selectedProgramData) {
      return undefined;
    }

    if (!referralEventId) {
      setEvents([]);
      setSelectedEvent(null);
      setBaseValues((previous) => ({
        ...previous,
        event_id: null,
        message: "",
      }));
      return undefined;
    }

    const loadReferralEvent = async () => {
      setLoadingEvent(true);

      try {
        const referralEvent = await getEvent(organization.id, selectedProgramId, referralEventId);

        if (!isMounted) return;

        setEvents(referralEvent ? labelizeNamedData([referralEvent]) : []);
        setSelectedEvent(referralEvent || null);
      } catch (error) {
        if (!isMounted) return;
        setEvents([]);
        setSelectedEvent(null);
      } finally {
        if (isMounted) {
          setLoadingEvent(false);
        }
      }
    };

    loadReferralEvent();

    return () => {
      isMounted = false;
    };
  }, [organization?.id, selectedProgramId, selectedProgramData, referralEventId]);

  const selectedEventOption = useMemo(() => {
    if (!selectedEvent) return null;
    return {
      label: selectedEvent.name,
      value: String(selectedEvent.id),
    };
  }, [selectedEvent]);

  useEffect(() => {
    setBaseValues((previous) => ({
      ...previous,
      program: selectedProgram,
      event_id: selectedEventOption,
      message: getAwardMessage(selectedEvent),
    }));
  }, [selectedEvent, selectedEventOption, selectedProgram]);

  const selectedProgramLabel = useMemo(() => {
    if (selectedProgram?.label) {
      return stripProgramPrefix(selectedProgram.label);
    }

    return activeProgram?.name || rootProgram?.name || "No program selected";
  }, [activeProgram?.name, rootProgram?.name, selectedProgram]);

  const selectedProgramMeta = useMemo(() => {
    if (unitNumberOptions.length > 0) {
      return `${unitNumberOptions.length.toLocaleString()} unit numbers available`;
    }

    return "";
  }, [unitNumberOptions]);

  const isVariableAmountAward =
    selectedEvent &&
    !isBadgeAward(selectedEvent.event_type_id) &&
    !isCustomAward(selectedEvent.event_type_id);

  const validateForm = (values) => {
    const errors = {};

    if (!values.program) {
      errors.program = "Select a program";
    }
    if (!values.email) {
      errors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = "Invalid email address";
    }
    if (!values.first_name) {
      errors.first_name = "First name is required";
    }
    if (!values.last_name) {
      errors.last_name = "Last name is required";
    }
    if (!values.event_id) {
      errors.event_id = "Select an event";
    }
    if (isVariableAmountAward) {
      if (!values.override_cash_value) {
        errors.override_cash_value = "This field is required";
      } else if (
        isNaN(parseFloat(values.override_cash_value)) ||
        parseFloat(values.override_cash_value) <= 0
      ) {
        errors.override_cash_value = "Enter a valid amount";
      } else if (
        selectedEvent?.max_awardable_amount &&
        parseFloat(values.override_cash_value) > parseFloat(selectedEvent.max_awardable_amount)
      ) {
        errors.override_cash_value = `Value cannot exceed ${toCurrency(
          selectedEvent.max_awardable_amount
        )}`;
      }
    }
    if (!values.message) {
      errors.message = "Message is required";
    }

    return errors;
  };

  const handleProgramSelect = (selectedOption, input, form) => {
    const currentValues = form.getState().values;
    const resetValues = {
      first_name: currentValues.first_name || "",
      last_name: currentValues.last_name || "",
      email: currentValues.email || "",
      external_id: currentValues.external_id || "",
      program: selectedOption,
      award_level: null,
      unit_number: null,
      event_id: null,
      override_cash_value: "",
      message: "",
      notes: "",
      restrictions: "",
      referrer: "",
    };

    setSubmissionMessage("");
    setSelectedProgram(selectedOption);
    setSelectedEvent(null);
    setEvents([]);
    setBaseValues(resetValues);
    input.onChange(selectedOption);
    form.restart(resetValues);
  };

  const onSubmit = async (values, form) => {
    if (!selectedProgram?.value || !selectedEvent?.id) {
      return;
    }

    let normalizedValues = { ...values };
    if (typeof normalizedValues.unit_number === "object") {
      normalizedValues = unpatchSelect(normalizedValues, ["unit_number"]);
    }
    if (typeof normalizedValues.award_level === "object") {
      normalizedValues = unpatchSelect(normalizedValues, ["award_level"]);
    }

    const invitePayload = {
      first_name: normalizedValues.first_name,
      last_name: normalizedValues.last_name,
      email: normalizedValues.email,
      unit_number: normalizedValues.unit_number || null,
      award_level: normalizedValues.award_level || null,
      invite_and_award: 1,
    };

    let awardAmount = null;
    if (isVariableAmountAward) {
      awardAmount = parseFloat(normalizedValues.override_cash_value);
    } else if (!isBadgeAward(selectedEvent.event_type_id)) {
      awardAmount = parseFloat(selectedEvent.max_awardable_amount || 0);
    }

    setLoading(true);
    setSubmitPhase("checking");
    setSubmissionMessage("");

    if (isPayInAdvance) {
      const currentBalance = await refreshBalance();

      if (!isNaN(awardAmount) && awardAmount !== null && awardAmount > currentBalance) {
        flash422(dispatch, {
          message: `Insufficient program balance. Available balance is ${toCurrency(
            currentBalance
          )}, but the award amount is ${toCurrency(awardAmount)}.`,
        });
        setLoading(false);
        setSubmitPhase("");
        return;
      }
    }

    setSubmitPhase("inviting");

    try {
      const inviteResponse = await axios.put(
        `/organization/${organization.id}/program/${selectedProgram.value}/invite`,
        invitePayload
      );

      // The global axios interceptor resolves (rather than rejects) 4xx/5xx
      // responses and already flashes the real error message itself — bail
      // out here instead of treating this as a success.
      if (isHandledErrorResponse(inviteResponse)) {
        return;
      }

      const newUser = inviteResponse?.data?.user;

      if (!newUser?.id) {
        throw new Error("Unable to create the invited user.");
      }

      setSubmitPhase("awarding");

      const awardPayload = {
        user_id: [newUser.id],
        event_id: selectedEvent.id,
        notes: normalizedValues.notes || null,
        message: normalizedValues.message,
        restrictions: normalizedValues.restrictions || null,
        override_cash_value: isVariableAmountAward
          ? normalizedValues.override_cash_value || null
          : null,
        referrer: normalizedValues.referrer || null,
        invite_and_award: 1,
      };

      const awardResponse = await axios.post(
        `/organization/${organization.id}/program/${selectedProgram.value}/award`,
        awardPayload
      );

      if (isHandledErrorResponse(awardResponse)) {
        return;
      }

      const resetValues = {
        program: selectedProgram,
        event_id: selectedEventOption,
        message: getAwardMessage(selectedEvent),
        award_level: null,
        unit_number: null,
      };

      setBaseValues(resetValues);
      setSubmissionMessage(
        `Invitation and award sent to ${normalizedValues.email}.`
      );
      flashSuccess(
        dispatch,
        "The user has been invited to your program and awarded successfully!"
      );
      form.restart(resetValues);
    } catch (error) {
      flash422(dispatch, error?.response?.data || error);
    } finally {
      setLoading(false);
      setSubmitPhase("");
      refreshBalance();
    }
  };

  const submitButtonText = loading
    ? submitPhase === "checking"
      ? "Checking balance..."
      : submitPhase === "inviting"
      ? "Inviting..."
      : "Awarding..."
    : "Send Award";

  if (!organization?.id || !rootProgram?.id) {
    return (
      <div className="manager-home manager-award-page">
        <Container className="manager-home__shell manager-home__content">
          <div className="manager-card manager-card--loading">
            Loading award workspace...
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="manager-home manager-award-page">
      <Container className="manager-home__shell manager-home__content">
        <div className="manager-award">
          <section className="manager-card manager-award__hero">
            <div className="manager-award__hero-copy">
              <span className="manager-card__eyebrow">Award</span>
              <h1 className="manager-award__title">Award New Participant</h1>
              <p className="manager-award__description">
                Invite a user and send their first award in one step.
              </p>
            </div>
          </section>

          <div className="manager-award__layout">
            <article className="manager-card manager-award__panel">
              <div className="manager-award__panel-head">
                <div>
                  <span className="manager-card__eyebrow">Award</span>
                  <h2 className="manager-card__title">Award Details</h2>
                </div>
              </div>
              <p className="manager-award__panel-description">
                Select a program, enter the user details, and send the award.
              </p>

              <div className="manager-award__panel-body">
                {submissionMessage && (
                  <div
                    className="manager-award__status is-success"
                    role="status"
                    aria-live="polite"
                  >
                    <strong>Award sent</strong>
                    <span>{submissionMessage}</span>
                  </div>
                )}

                {loadingPrograms && (
                  <div className="manager-award__empty">
                    Loading available programs...
                  </div>
                )}

                {!loadingPrograms && programOptions.length === 0 && (
                  <div className="manager-award__empty">
                    No assignable programs are available for this action.
                  </div>
                )}

                {!loadingPrograms && programOptions.length > 0 && (
                  <Form
                    onSubmit={onSubmit}
                    initialValues={baseValues}
                    keepDirtyOnReinitialize
                    validate={validateForm}
                  >
                    {({ handleSubmit, form }) => (
                      <form className="manager-award__form" onSubmit={handleSubmit}>
                        <div className="manager-award__summary">
                          <div className="manager-award__summary-item">
                            <span className="manager-card__eyebrow">Selected Program</span>
                            <strong className="manager-award__summary-value">
                              {selectedProgramLabel}
                            </strong>
                            {selectedProgramMeta && (
                              <span className="manager-award__summary-meta">
                                {selectedProgramMeta}
                              </span>
                            )}
                          </div>
                          {isPayInAdvance && (
                            <div className="manager-award__summary-item">
                              <span className="manager-card__eyebrow">Available Balance</span>
                              <strong className="manager-award__summary-value">
                                {toCurrency(balance)}
                              </strong>
                            </div>
                          )}
                        </div>

                        <section className="manager-award__section">
                          <div className="manager-award__section-head">
                            <span className="manager-card__eyebrow">Program</span>
                            <h3 className="manager-card__title">Assignment</h3>
                          </div>

                          <div className="manager-award__grid">
                            <Field name="program">
                              {({ input, meta }) => (
                                <div className="manager-award__field manager-award__field--full">
                                  <label className="manager-award__field-label">
                                    Reward Program
                                    <span className="manager-award__required">*</span>
                                  </label>
                                  <Select
                                    options={programOptions}
                                    isClearable={false}
                                    isLoading={loadingPrograms}
                                    className="react-select"
                                    classNamePrefix="react-select"
                                    placeholder="Select a reward program"
                                    value={input.value || selectedProgram}
                                    onChange={(selectedOption) =>
                                      handleProgramSelect(selectedOption, input, form)
                                    }
                                    onBlur={() => input.onBlur(input.value)}
                                  />
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            {awardLevelOptions.length > 0 && (
                              <Field name="award_level">
                                {({ input, meta }) => (
                                  <div className="manager-award__field">
                                    <label className="manager-award__field-label">
                                      Award Level
                                    </label>
                                    <Select
                                      options={awardLevelOptions}
                                      isClearable
                                      className="react-select"
                                      classNamePrefix="react-select"
                                      placeholder="Optional award level"
                                      value={input.value || null}
                                      onChange={input.onChange}
                                      onBlur={() => input.onBlur(input.value)}
                                    />
                                    {meta.touched && meta.error && (
                                      <span className="form-error">{meta.error}</span>
                                    )}
                                  </div>
                                )}
                              </Field>
                            )}

                            {unitNumberOptions.length > 0 && (
                              <Field name="unit_number">
                                {({ input, meta }) => (
                                  <div className="manager-award__field">
                                    <label className="manager-award__field-label">
                                      Unit Number
                                    </label>
                                    <Select
                                      options={unitNumberOptions}
                                      isClearable
                                      className="react-select"
                                      classNamePrefix="react-select"
                                      placeholder="Optional unit assignment"
                                      value={input.value || null}
                                      onChange={input.onChange}
                                      onBlur={() => input.onBlur(input.value)}
                                    />
                                    {meta.touched && meta.error && (
                                      <span className="form-error">{meta.error}</span>
                                    )}
                                  </div>
                                )}
                              </Field>
                            )}
                          </div>
                        </section>

                        <section className="manager-award__section">
                          <div className="manager-award__section-head">
                            <span className="manager-card__eyebrow">User</span>
                            <h3 className="manager-card__title">User Details</h3>
                          </div>

                          <div className="manager-award__grid">
                            <Field name="first_name">
                              {({ input, meta }) => (
                                <div className="manager-award__field">
                                  <label className="manager-award__field-label">
                                    First Name
                                    <span className="manager-award__required">*</span>
                                  </label>
                                  <FormGroup>
                                    <Input
                                      className="manager-award__input"
                                      placeholder="Enter first name"
                                      type="text"
                                      {...input}
                                    />
                                  </FormGroup>
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            <Field name="last_name">
                              {({ input, meta }) => (
                                <div className="manager-award__field">
                                  <label className="manager-award__field-label">
                                    Last Name
                                    <span className="manager-award__required">*</span>
                                  </label>
                                  <FormGroup>
                                    <Input
                                      className="manager-award__input"
                                      placeholder="Enter last name"
                                      type="text"
                                      {...input}
                                    />
                                  </FormGroup>
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            <Field name="email">
                              {({ input, meta }) => (
                                <div className="manager-award__field">
                                  <label className="manager-award__field-label">
                                    Email Address
                                    <span className="manager-award__required">*</span>
                                  </label>
                                  <FormGroup>
                                    <Input
                                      className="manager-award__input"
                                      placeholder="name@company.com"
                                      type="email"
                                      {...input}
                                    />
                                  </FormGroup>
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            <Field name="external_id">
                              {({ input, meta }) => (
                                <div className="manager-award__field">
                                  <label className="manager-award__field-label">
                                    External ID
                                  </label>
                                  <FormGroup>
                                    <Input
                                      className="manager-award__input"
                                      placeholder="Optional employee or participant ID"
                                      type="text"
                                      {...input}
                                    />
                                  </FormGroup>
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>
                          </div>
                        </section>

                        <section className="manager-award__section">
                          <div className="manager-award__section-head">
                            <span className="manager-card__eyebrow">Award</span>
                            <h3 className="manager-card__title">Award Details</h3>
                          </div>

                          <div className="manager-award__grid">
                            <Field name="event_id">
                              {({ input, meta }) => (
                                <div className="manager-award__field manager-award__field--full">
                                  <label className="manager-award__field-label">
                                    Event
                                    <span className="manager-award__required">*</span>
                                  </label>
                                  <Select
                                    options={events}
                                    isClearable={false}
                                    isDisabled
                                    isLoading={loadingEvent}
                                    className="react-select"
                                    classNamePrefix="react-select"
                                    placeholder="No Referral Submission Award event configured"
                                    value={input.value || selectedEventOption}
                                    onBlur={() => input.onBlur(input.value)}
                                  />
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                  {loadingEvent && (
                                    <span className="manager-award__loading-note">
                                      Loading event details...
                                    </span>
                                  )}
                                  {!loadingEvent && events.length === 0 && (
                                    <span className="manager-award__loading-note">
                                      This program has no Referral Submission Award event configured.
                                    </span>
                                  )}
                                </div>
                              )}
                            </Field>

                            {isVariableAmountAward && (
                              <>
                                <div className="manager-award__field">
                                  <label className="manager-award__field-label">
                                    Maximum Cash Value
                                  </label>
                                  <div className="manager-award__stat">
                                    {toCurrency(selectedEvent.max_awardable_amount)}
                                  </div>
                                </div>

                                <Field name="override_cash_value">
                                  {({ input, meta }) => (
                                    <div className="manager-award__field">
                                      <label className="manager-award__field-label">
                                        Custom Cash Value
                                        <span className="manager-award__required">*</span>
                                      </label>
                                      <FormGroup>
                                        <Input
                                          className="manager-award__input"
                                          placeholder={`Enter an amount under ${toCurrency(
                                            selectedEvent.max_awardable_amount
                                          )}`}
                                          type="text"
                                          {...input}
                                        />
                                      </FormGroup>
                                      {meta.touched && meta.error && (
                                        <span className="form-error">{meta.error}</span>
                                      )}
                                    </div>
                                  )}
                                </Field>
                              </>
                            )}

                            <Field name="message">
                              {({ input, meta }) => (
                                <div className="manager-award__field manager-award__field--full">
                                  <label className="manager-award__field-label">
                                    Award Message
                                    <span className="manager-award__required">*</span>
                                  </label>
                                  <FormGroup>
                                    <Input
                                      className="manager-award__input manager-award__textarea"
                                      placeholder="Enter the award message"
                                      type="textarea"
                                      readOnly={selectedEvent ? !selectedEvent.award_message_editable : false}
                                      {...input}
                                    />
                                  </FormGroup>
                                  {meta.touched && meta.error && (
                                    <span className="form-error">{meta.error}</span>
                                  )}
                                </div>
                              )}
                            </Field>
                          </div>

                          <details className="manager-award__details">
                            <summary className="manager-award__details-summary">
                              Additional Details
                            </summary>
                            <div className="manager-award__details-body">
                              <div className="manager-award__grid">
                                <Field name="notes">
                                  {({ input, meta }) => (
                                    <div className="manager-award__field manager-award__field--full">
                                      <label className="manager-award__field-label">
                                        Notes
                                      </label>
                                      <FormGroup>
                                        <Input
                                          className="manager-award__input manager-award__textarea"
                                          placeholder="Optional award notes"
                                          type="textarea"
                                          {...input}
                                        />
                                      </FormGroup>
                                      {meta.touched && meta.error && (
                                        <span className="form-error">{meta.error}</span>
                                      )}
                                    </div>
                                  )}
                                </Field>

                                {isCustomAward(selectedEvent?.event_type_id) && (
                                  <Field name="restrictions">
                                    {({ input, meta }) => (
                                      <div className="manager-award__field manager-award__field--full">
                                        <label className="manager-award__field-label">
                                          Restrictions
                                        </label>
                                        <FormGroup>
                                          <Input
                                            className="manager-award__input manager-award__textarea"
                                            placeholder="Optional restrictions"
                                            type="textarea"
                                            {...input}
                                          />
                                        </FormGroup>
                                        {meta.touched && meta.error && (
                                          <span className="form-error">{meta.error}</span>
                                        )}
                                      </div>
                                    )}
                                  </Field>
                                )}

                                <Field name="referrer">
                                  {({ input, meta }) => (
                                    <div className="manager-award__field">
                                      <label className="manager-award__field-label">
                                        Referrer
                                      </label>
                                      <FormGroup>
                                        <Input
                                          className="manager-award__input"
                                          placeholder="Optional referrer"
                                          type="text"
                                          {...input}
                                        />
                                      </FormGroup>
                                      {meta.touched && meta.error && (
                                        <span className="form-error">{meta.error}</span>
                                      )}
                                    </div>
                                  )}
                                </Field>

                                <Field name="doc_file">
                                  {({ input, meta }) => {
                                    const { value, ...restInput } = input;
                                    return (
                                      <div className="manager-award__field">
                                        <label className="manager-award__field-label">
                                          Documentation
                                        </label>
                                        <FormGroup>
                                          <Input
                                            className="manager-award__input manager-award__file"
                                            type="file"
                                            {...restInput}
                                          />
                                        </FormGroup>
                                        {meta.touched && meta.error && (
                                          <span className="form-error">{meta.error}</span>
                                        )}
                                      </div>
                                    );
                                  }}
                                </Field>
                              </div>
                            </div>
                          </details>
                        </section>

                        <div className="manager-award__footer">
                          <p className="manager-award__footer-copy">
                            The invitation and award are sent together when submitted.
                          </p>
                          <TemplateButton
                            className="manager-home__send-btn manager-award__submit"
                            type="submit"
                            text={submitButtonText}
                            disabled={loading || loadingPrograms || loadingEvent || events.length === 0}
                            spinner={loading}
                          />
                        </div>
                      </form>
                    )}
                  </Form>
                )}
              </div>
            </article>
          </div>
        </div>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    rootProgram: state.rootProgram,
    organization: state.organization,
    activeProgram: state.program,
  };
};

export default connect(mapStateToProps)(AwardNewParticipant);
